import { useCallback, useRef, useState } from 'react'

import { F0Box, F0Button, F0Dialog, F0Icon, F0Text } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { AlignTextJustify, Delete, TextSize } from '@factorialco/f0-react/icons/app'
import { Box, RadioButton } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import type { SectionType } from './types'

type FieldTypeOption = 'text' | 'select'

type Props = {
  section: SectionType
  onClose: () => void
  onAdd: (
    section: SectionType,
    label: string,
    fieldType: FieldTypeOption,
    options: string[]
  ) => void
}

type DropdownOption = { id: number; value: string }

function AddFieldModal({ section, onClose, onAdd }: Props) {
  const [fieldName, setFieldName] = useState('')
  const [fieldType, setFieldType] = useState<FieldTypeOption>('text')
  const nextOptionId = useRef(1)
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([{ id: 0, value: '' }])

  const handleFieldTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (value === 'text' || value === 'select') {
      setFieldType(value)
    }
  }, [])

  const handleOptionChange = useCallback((optionId: number, value: string) => {
    setDropdownOptions((prev) => prev.map((opt) => (opt.id === optionId ? { ...opt, value } : opt)))
  }, [])

  const handleDeleteOption = useCallback((optionId: number) => {
    setDropdownOptions((prev) => prev.filter((opt) => opt.id !== optionId))
  }, [])

  const handleAddOption = useCallback(() => {
    const id = nextOptionId.current
    nextOptionId.current += 1
    setDropdownOptions((prev) => [...prev, { id, value: '' }])
  }, [])

  const isValid =
    fieldName.trim().length > 0 &&
    (fieldType === 'text' || dropdownOptions.filter((o) => o.value.trim().length > 0).length >= 1)

  const handleAdd = useCallback(() => {
    const trimmed = fieldName.trim()
    if (!trimmed) return
    const options =
      fieldType === 'select'
        ? dropdownOptions.map((o) => o.value.trim()).filter((o) => o.length > 0)
        : []
    onAdd(section, trimmed, fieldType, options)
    onClose()
  }, [fieldName, fieldType, dropdownOptions, section, onAdd, onClose])

  const optionPlaceholder = (index: number) =>
    `${i18n.t('procurement.purchase_requests.settings.po_templates.detail.option_label')} ${index + 1}`

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      width="sm"
      title={i18n.t('procurement.purchase_requests.settings.po_templates.detail.add_field_title')}
      primaryAction={{
        label: i18n.t('common.add'),
        onClick: handleAdd,
        disabled: !isValid,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="lg">
        {/* Field type selector */}
        <F0Box display="flex" flexDirection="row" gap="sm">
          <Box component="label" extra={{ htmlFor: 'fieldType-text' }} flexGrow cursor="pointer">
            <F0Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="sm"
              border={fieldType === 'text' ? 'thick' : 'default'}
              borderRadius="md"
              padding="sm"
            >
              <F0Icon icon={TextSize} size="md" />
              <F0Text
                content={i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.field_type_text'
                )}
                variant="body"
              />
              <F0Box grow display="flex" justifyContent="end">
                <RadioButton
                  name="fieldType"
                  id="fieldType-text"
                  value="text"
                  checked={fieldType === 'text'}
                  onChange={handleFieldTypeChange}
                  label=""
                />
              </F0Box>
            </F0Box>
          </Box>

          <Box component="label" extra={{ htmlFor: 'fieldType-select' }} flexGrow cursor="pointer">
            <F0Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="sm"
              border={fieldType === 'select' ? 'thick' : 'default'}
              borderRadius="md"
              padding="sm"
            >
              <F0Icon icon={AlignTextJustify} size="md" />
              <F0Text
                content={i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.field_type_select'
                )}
                variant="body"
              />
              <F0Box grow display="flex" justifyContent="end">
                <RadioButton
                  name="fieldType"
                  id="fieldType-select"
                  value="select"
                  checked={fieldType === 'select'}
                  onChange={handleFieldTypeChange}
                  label=""
                />
              </F0Box>
            </F0Box>
          </Box>
        </F0Box>

        {/* Field name */}
        <Input
          label={i18n.t(
            'procurement.purchase_requests.settings.po_templates.detail.field_name_placeholder'
          )}
          value={fieldName}
          onChange={setFieldName}
          placeholder={i18n.t(
            'procurement.purchase_requests.settings.po_templates.detail.field_name_placeholder'
          )}
          required
          autoFocus
        />

        {/* Dropdown options editor (only for select type) */}
        {fieldType === 'select' && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              content={i18n.t(
                'procurement.purchase_requests.settings.po_templates.detail.dropdown_options'
              )}
              variant="body"
            />

            {dropdownOptions.map((option, index) => (
              <F0Box
                key={option.id}
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="md"
                paddingBottom="md"
              >
                <F0Box grow>
                  <Input
                    label={optionPlaceholder(index)}
                    hideLabel
                    value={option.value}
                    onChange={(value: string) => handleOptionChange(option.id, value)}
                    placeholder={optionPlaceholder(index)}
                  />
                </F0Box>
                <F0Button
                  icon={Delete}
                  label={i18n.t('common.delete')}
                  hideLabel
                  variant="outline"
                  size="md"
                  onClick={() => handleDeleteOption(option.id)}
                />
              </F0Box>
            ))}

            <F0Box alignItems="start" paddingTop="sm">
              <F0Button
                label={i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.new_option'
                )}
                variant="outline"
                size="md"
                onClick={handleAddOption}
              />
            </F0Box>
          </F0Box>
        )}
      </F0Box>
    </F0Dialog>
  )
}

export default AddFieldModal
